import type { IssueTypes } from '@risksmart-app/trpc/src/services/service.types';
import type {
  InsertChildIssueMutation,
  InsertIssueInput,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type ReportPublicIssueTRPCInput = Omit<InsertIssueInput, 'Type'> & {
  Type: IssueTypes;
};

/**
 * Maps the tRPC response to the GraphQL mutation shape the report-an-issue
 * page consumes.
 */
function mapTrpcResponseToGraphQL(trpcData: {
  Id: string;
  SequentialId: number | null;
}): InsertChildIssueMutation {
  return {
    insertChildIssue: {
      __typename: 'InsertChildIssueOutput',
      Id: trpcData.Id,
      SequentialId: trpcData.SequentialId ?? 0,
    },
  };
}

/**
 * Raw tRPC hook for the public "report an issue" submit — routes to
 * `issue.reportPublic`, which is authorized on `public_issue_form:read` (not
 * `issue:insert`) and always creates a parent-less issue (COI-488). Not
 * imported outside this dir — consumers use the flag-gated `useReportPublicIssue`.
 */
export const useReportPublicIssueTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.issue.reportPublic.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.issue.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.issue.issueById.queryKey(),
        });
      },
    }),
  });

  return {
    reportPublicIssue: async (
      variables: ReportPublicIssueTRPCInput
    ): Promise<InsertChildIssueMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
